import type { PaginatedMeta, PaginationParams } from './client';
import { request, toQuery } from './client';

export type DurationUnit = 'days' | 'weeks' | 'months';

export type ProgramSession = {
	id: number;
	duration_minutes: number;
	repetition_count: number;
};

export type Program = {
	id: number;
	title: string;
	description: string;
	duration: { value: number; unit: DurationUnit };
	price: { amount: number; currency: string; is_free: boolean };
	max_enrollments: number;
	is_active: boolean;
	coach_id: number | null;
	coach: { id: number | null; name: string };
	booking_count: number;
	categories: Array<{ id: number; name: string; slug: string }>;
	session: ProgramSession | null;
	created_at: string;
	updated_at: string | null;
};

export type ProgramsListResponse = PaginatedMeta & { programs: Program[] };

export type CreateProgramData = {
	title: string;
	duration_value: number;
	duration_unit: DurationUnit;
	description?: string;
	price_amount?: number;
	price_currency?: string;
	max_enrollments?: number;
	is_active?: boolean;
	coach_id?: number;
	category_ids?: number[];
	session_duration_minutes?: number;
	session_repetition_count?: number;
};

export type UpdateProgramData = Partial<CreateProgramData> & {
	remove_session?: boolean;
};

export type ProgramsListParams = PaginationParams & {
	search?: string;
	status?: 'active' | 'draft' | 'any';
	category_id?: number;
};

export type BulkAction = 'delete' | 'activate' | 'deactivate';

export type BulkResult = {
	action: BulkAction;
	processed: number;
	failed: number;
	results: Array<{ id: number; success: boolean; error?: string }>;
};

export const programsApi = {
	list: (params?: ProgramsListParams) =>
		request<ProgramsListResponse>('/programs' + toQuery(params)),

	get: (id: number) => request<Program>(`/programs/${id}`),

	create: (data: CreateProgramData) =>
		request<Program>('/programs', { method: 'POST', data }),

	update: (id: number, data: UpdateProgramData) =>
		request<Program>(`/programs/${id}`, { method: 'POST', data }),

	delete: (id: number) =>
		request<{ deleted: boolean; id: number }>(`/programs/${id}`, {
			method: 'DELETE',
		}),

	bulk: (action: BulkAction, ids: number[]) =>
		request<BulkResult>('/programs/bulk', { method: 'POST', data: { action, ids } }),
};
